/**
 * Side-by-side timber compare & pick — select individual lines from the
 * proposal and/or a CSV, name a new structure, and create it.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  ChevronRight,
  ChevronDown,
  FileText,
  ChevronsUpDown,
  AlertTriangle,
} from 'lucide-react';
import type { CSVParseResult } from '../../utils/proposalCSVParser';
import type { ProposalNodeSet } from '../../types';
import {
  buildCompareConnectorPairs,
  buildCompareDisplayGroups,
  groupCsvItemsForDisplay,
  groupProposalTimberForDisplay,
  timberRowMatchKey,
  type CompareAlignedSlot,
  type CompareDisplayGroup,
} from '../../utils/proposalTimberComparePick';
import { normalizeStructureName } from '../../utils/proposalTimberDiff';
import type { TimberSourceOption } from './TimberListUpdateModal';
import './TimberComparePickModal.css';

export interface TimberComparePickPayload {
  structureName: string;
  /** When set, append picked lines to this structure instead of creating a new one. */
  targetNodeSetId?: string;
  proposalLineIds: string[];
  csvLineKeys: string[];
}

export interface TimberComparePickModalProps {
  nodeSets: ProposalNodeSet[];
  parsed: CSVParseResult;
  source?: { fileName?: string; uploadedAt?: Date | null; uploadedByName?: string };
  sourceOptions?: TimberSourceOption[];
  selectedSourceId?: string;
  appliedSourceId?: string | null;
  onSelectSource?: (fileId: string) => void;
  sourceSwitchLoading?: boolean;
  readOnly?: boolean;
  /** Modal overlay (default) vs inline panel for supplier RFQ / other embeds. */
  variant?: 'modal' | 'embedded';
  /** View-only comparison — hides pick controls, toolbar, and footer. */
  compareOnly?: boolean;
  proposalPanelTitle?: string;
  csvPanelTitle?: string;
  modalTitle?: string;
  /** When true, released/CSV panel is left and proposal panel is right. */
  swapPanels?: boolean;
  onCancel: () => void;
  onCreate: (payload: TimberComparePickPayload) => void;
  /** Lock destination to this structure (Add Timber from a structure card). */
  lockToNodeSetId?: string;
}

const GroupSelectCheckbox: React.FC<{
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
}> = ({ checked, indeterminate, disabled, label, onChange }) => (
  <input
    type="checkbox"
    className="tcp-group-check"
    checked={checked}
    ref={(el) => {
      if (el) el.indeterminate = indeterminate;
    }}
    onChange={onChange}
    onClick={(e) => e.stopPropagation()}
    disabled={disabled}
    title={label}
    aria-label={label}
  />
);

const fmtDateShort = (d?: Date | null): string => {
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).format(d);
  } catch {
    return d.toString();
  }
};

interface SourceHeaderProps {
  source?: { fileName?: string; uploadedAt?: Date | null; uploadedByName?: string };
  sourceOptions?: TimberSourceOption[];
  selectedSourceId?: string;
  appliedSourceId?: string | null;
  onSelectSource?: (fileId: string) => void;
  sourceSwitchLoading?: boolean;
  readOnly?: boolean;
  onPopoverOpenChange?: (open: boolean) => void;
}

const SourceHeader: React.FC<SourceHeaderProps> = ({
  source,
  sourceOptions,
  selectedSourceId,
  appliedSourceId,
  onSelectSource,
  sourceSwitchLoading,
  readOnly,
  onPopoverOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const setPopoverOpen = (next: boolean) => {
    setOpen(next);
    onPopoverOpenChange?.(next);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const showPicker = !readOnly && sourceOptions && sourceOptions.length > 1 && onSelectSource;

  if (!showPicker) {
    return (
      <p className="tcp-source">
        {source?.fileName ? <strong>{source.fileName}</strong> : 'Imported CSV'}
        {source?.uploadedByName ? <> &middot; uploaded by {source.uploadedByName}</> : null}
        {source?.uploadedAt ? <> &middot; {fmtDateShort(source.uploadedAt)}</> : null}
      </p>
    );
  }

  const selectedOption = selectedSourceId
    ? sourceOptions.find(f => f.id === selectedSourceId)
    : undefined;
  const displayName = selectedOption?.name || source?.fileName || 'Imported CSV';
  const displayUploader = selectedOption?.uploadedByName || source?.uploadedByName;
  const displayDate = selectedOption?.uploadedAt || source?.uploadedAt;

  return (
    <div className="tcp-source-picker" ref={wrapRef}>
      <button
        type="button"
        className="tcp-source-trigger"
        onClick={() => setPopoverOpen(!open)}
        disabled={sourceSwitchLoading}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Change CSV source"
      >
        <span className="tcp-source-trigger-text">
          <strong>{displayName}</strong>
          {displayUploader && <> &middot; uploaded by {displayUploader}</>}
          {displayDate && <> &middot; {fmtDateShort(displayDate)}</>}
        </span>
        <span className="tcp-source-trigger-action">
          {sourceSwitchLoading ? (
            <>
              <span className="tcp-source-spinner" aria-hidden />
              Loading…
            </>
          ) : (
            <>
              Change source
              <ChevronsUpDown size={14} aria-hidden />
            </>
          )}
        </span>
      </button>
      {open && !sourceSwitchLoading && (
        <div className="tcp-source-popover" role="listbox" aria-label="Timber CSV versions">
          {sourceOptions.map((file, idx) => {
            const isSelected = file.id === selectedSourceId;
            const isLatest = idx === 0;
            const isApplied = !!appliedSourceId && appliedSourceId === file.id;
            return (
              <button
                key={file.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`tcp-source-option${isSelected ? ' tcp-source-option--selected' : ''}`}
                onClick={() => {
                  if (file.id && file.id !== selectedSourceId) {
                    onSelectSource(file.id);
                  }
                  setPopoverOpen(false);
                }}
              >
                <span className="tcp-source-option-icon" aria-hidden>
                  <FileText size={16} />
                </span>
                <span className="tcp-source-option-body">
                  <span className="tcp-source-option-title">
                    <span className="tcp-source-option-name">{file.name}</span>
                    {isLatest && <span className="tcp-source-badge tcp-source-badge--latest">Latest</span>}
                    {isApplied && <span className="tcp-source-badge tcp-source-badge--applied">Applied</span>}
                    {!isLatest && file.versionLabel && (
                      <span className="tcp-source-badge tcp-source-badge--version">{file.versionLabel}</span>
                    )}
                  </span>
                  <span className="tcp-source-option-meta">
                    {fmtDateShort(file.uploadedAt)}
                    {file.uploadedByName && (
                      <>
                        <span className="tcp-source-option-meta-dot">·</span>
                        by {file.uploadedByName}
                      </>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TimberComparePickModal: React.FC<TimberComparePickModalProps> = ({
  nodeSets,
  parsed,
  source,
  sourceOptions,
  selectedSourceId,
  appliedSourceId,
  onSelectSource,
  sourceSwitchLoading,
  readOnly,
  variant = 'modal',
  compareOnly = false,
  proposalPanelTitle = 'Proposal',
  csvPanelTitle = 'CSV',
  modalTitle,
  swapPanels = false,
  onCancel,
  onCreate,
  lockToNodeSetId,
}) => {
  const isEmbedded = variant === 'embedded';
  const isCompareOnly = compareOnly || isEmbedded;
  const resolvedModalTitle =
    modalTitle ?? (isCompareOnly ? 'Compare timber lists' : 'Compare & Pick Timber Lines');
  const leftPanelTitle = swapPanels ? csvPanelTitle : proposalPanelTitle;
  const rightPanelTitle = swapPanels ? proposalPanelTitle : csvPanelTitle;
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [targetMode, setTargetMode] = useState<'new' | 'existing'>(
    lockToNodeSetId ? 'existing' : 'new',
  );
  const [targetNodeSetId, setTargetNodeSetId] = useState(lockToNodeSetId ?? '');
  const [structureName, setStructureName] = useState('');
  const [proposalSelected, setProposalSelected] = useState<Set<string>>(() => new Set());
  const [csvSelected, setCsvSelected] = useState<Set<string>>(() => new Set());
  const [collapsedProposal, setCollapsedProposal] = useState<Set<string>>(() => new Set());
  const [collapsedCsv, setCollapsedCsv] = useState<Set<string>>(() => new Set());
  const [collapsedCompare, setCollapsedCompare] = useState<Set<string>>(() => new Set());
  const [sourcePopoverOpen, setSourcePopoverOpen] = useState(false);
  const panelsWrapRef = useRef<HTMLDivElement>(null);
  const proposalRowRefs = useRef<Map<string, HTMLLabelElement>>(new Map());
  const csvRowRefs = useRef<Map<string, HTMLLabelElement>>(new Map());
  const [connectorPaths, setConnectorPaths] = useState<Array<{ id: string; d: string }>>([]);

  const proposalGroups = useMemo(
    () => groupProposalTimberForDisplay({ nodeSets }),
    [nodeSets],
  );
  const csvGroups = useMemo(() => groupCsvItemsForDisplay(parsed), [parsed]);
  const compareDisplayGroups = useMemo(
    () => (isCompareOnly ? buildCompareDisplayGroups(proposalGroups, csvGroups) : []),
    [isCompareOnly, proposalGroups, csvGroups],
  );

  const collapsedProposalForConnectors = useMemo(() => {
    if (!isCompareOnly) return collapsedProposal;
    const next = new Set<string>();
    for (const group of compareDisplayGroups) {
      if (collapsedCompare.has(group.key)) next.add(group.nodeSetId);
    }
    return next;
  }, [isCompareOnly, collapsedProposal, collapsedCompare, compareDisplayGroups]);

  const collapsedCsvForConnectors = useMemo(() => {
    if (!isCompareOnly) return collapsedCsv;
    const next = new Set<string>();
    for (const group of compareDisplayGroups) {
      if (collapsedCompare.has(group.key) && group.csvGroupName) {
        next.add(group.csvGroupName);
      }
    }
    return next;
  }, [isCompareOnly, collapsedCsv, collapsedCompare, compareDisplayGroups]);

  const allProposalLineIds = useMemo(
    () => proposalGroups.flatMap(g => g.lines.map(l => l.id)),
    [proposalGroups],
  );
  const allCsvKeys = useMemo(
    () => csvGroups.flatMap(g => g.items.map(i => i.selectionKey)),
    [csvGroups],
  );

  const structureOptions = useMemo(
    () => nodeSets.map(ns => ({ id: ns.id, name: ns.name })),
    [nodeSets],
  );

  const selectedTargetStructure = useMemo(
    () => structureOptions.find(s => s.id === targetNodeSetId),
    [structureOptions, targetNodeSetId],
  );

  const totalSelected = proposalSelected.size + csvSelected.size;
  const canCreate =
    !readOnly &&
    totalSelected > 0 &&
    (targetMode === 'new'
      ? structureName.trim().length > 0
      : Boolean(targetNodeSetId));

  const connectorPairs = useMemo(
    () =>
      buildCompareConnectorPairs(
        proposalGroups,
        csvGroups,
        collapsedProposalForConnectors,
        collapsedCsvForConnectors,
      ),
    [proposalGroups, csvGroups, collapsedProposalForConnectors, collapsedCsvForConnectors],
  );

  const matchedProposalIds = useMemo(
    () => new Set(connectorPairs.map(p => p.proposalRowId)),
    [connectorPairs],
  );
  const matchedCsvKeys = useMemo(
    () => new Set(connectorPairs.map(p => p.csvRowKey)),
    [connectorPairs],
  );

  const duplicateMatchKeys = useMemo(() => {
    const proposalKeys = new Set<string>();
    const csvKeys = new Set<string>();
    for (const g of proposalGroups) {
      for (const line of g.lines) {
        if (proposalSelected.has(line.id)) {
          proposalKeys.add(timberRowMatchKey(line.name, line.dimensions, line.quantity));
        }
      }
    }
    for (const g of csvGroups) {
      for (const { item, selectionKey } of g.items) {
        if (csvSelected.has(selectionKey)) {
          csvKeys.add(timberRowMatchKey(item.pieceName, item.normalizedDimensions, item.quantity));
        }
      }
    }
    const dupes: string[] = [];
    proposalKeys.forEach((k) => {
      if (csvKeys.has(k)) dupes.push(k);
    });
    return dupes;
  }, [proposalGroups, csvGroups, proposalSelected, csvSelected]);

  const updateConnectors = useCallback(() => {
    const wrap = panelsWrapRef.current;
    if (!wrap || connectorPairs.length === 0) {
      setConnectorPaths([]);
      return;
    }
    const wrapRect = wrap.getBoundingClientRect();
    const paths: Array<{ id: string; d: string }> = [];

    for (const pair of connectorPairs) {
      const leftEl = swapPanels
        ? csvRowRefs.current.get(pair.csvRowKey)
        : proposalRowRefs.current.get(pair.proposalRowId);
      const rightEl = swapPanels
        ? proposalRowRefs.current.get(pair.proposalRowId)
        : csvRowRefs.current.get(pair.csvRowKey);
      if (!leftEl || !rightEl) continue;

      const l = leftEl.getBoundingClientRect();
      const r = rightEl.getBoundingClientRect();
      const x1 = l.right - wrapRect.left;
      const y1 = l.top + l.height / 2 - wrapRect.top;
      const x2 = r.left - wrapRect.left;
      const y2 = r.top + r.height / 2 - wrapRect.top;
      const midX = (x1 + x2) / 2;
      const d = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
      paths.push({ id: pair.id, d });
    }
    setConnectorPaths(paths);
  }, [connectorPairs, swapPanels]);

  useLayoutEffect(() => {
    updateConnectors();
    const wrap = panelsWrapRef.current;
    if (!wrap) return;

    const ro = new ResizeObserver(updateConnectors);
    ro.observe(wrap);

    const scrollEls = wrap.querySelectorAll('.tcp-panel-body');
    scrollEls.forEach(el => el.addEventListener('scroll', updateConnectors, { passive: true }));
    window.addEventListener('resize', updateConnectors);

    return () => {
      ro.disconnect();
      scrollEls.forEach(el => el.removeEventListener('scroll', updateConnectors));
      window.removeEventListener('resize', updateConnectors);
    };
  }, [updateConnectors, collapsedProposal, collapsedCsv, collapsedCompare, proposalGroups, csvGroups, compareDisplayGroups]);

  useEffect(() => {
    if (!lockToNodeSetId) return;
    setTargetMode('existing');
    setTargetNodeSetId(lockToNodeSetId);
  }, [lockToNodeSetId]);

  useEffect(() => {
    if (isCompareOnly || lockToNodeSetId) return;
    if (targetMode === 'new') {
      nameInputRef.current?.focus();
    }
  }, [isCompareOnly, targetMode, lockToNodeSetId]);

  useEffect(() => {
    if (lockToNodeSetId) return;
    if (targetMode !== 'existing' || targetNodeSetId || structureOptions.length === 0) return;
    if (csvSelected.size === 0) return;

    const selectedGroups = new Set<string>();
    for (const group of csvGroups) {
      if (group.items.some(item => csvSelected.has(item.selectionKey))) {
        selectedGroups.add(normalizeStructureName(group.groupName));
      }
    }
    if (selectedGroups.size !== 1) return;

    const [normName] = Array.from(selectedGroups);
    const match = structureOptions.find(s => normalizeStructureName(s.name) === normName);
    if (match) {
      setTargetNodeSetId(match.id);
    }
  }, [targetMode, targetNodeSetId, structureOptions, csvGroups, csvSelected]);

  useEffect(() => {
    if (isEmbedded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        if (sourcePopoverOpen) {
          e.preventDefault();
          setSourcePopoverOpen(false);
          return;
        }
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sourcePopoverOpen, onCancel, isEmbedded]);

  const toggleProposalLine = (id: string) => {
    setProposalSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCsvLine = (key: string) => {
    setCsvSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleProposalGroup = (nodeSetId: string) => {
    setCollapsedProposal(prev => {
      const next = new Set(prev);
      if (next.has(nodeSetId)) next.delete(nodeSetId);
      else next.add(nodeSetId);
      return next;
    });
  };

  const toggleCsvGroup = (groupName: string) => {
    setCollapsedCsv(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const toggleCompareGroup = (groupKey: string) => {
    setCollapsedCompare(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const renderProposalSlotRow = (
    slot: CompareAlignedSlot,
    group: CompareDisplayGroup,
    slotIndex: number,
  ) => {
    const line = slot.proposalLine;
    if (!line) {
      return (
        <li key={slot.slotKey}>
          <div
            className="tcp-line-row tcp-line-row--spacer"
            aria-hidden="true"
            data-slot={`${group.key}-${slotIndex}`}
          />
        </li>
      );
    }

    return (
      <li key={slot.slotKey}>
        <label
          ref={(el) => {
            if (el) proposalRowRefs.current.set(line.id, el);
            else proposalRowRefs.current.delete(line.id);
          }}
          className={`tcp-line-row${!isCompareOnly && proposalSelected.has(line.id) ? ' tcp-line-row--selected' : ''}${matchedProposalIds.has(line.id) ? ' tcp-line-row--matched' : ''}${isCompareOnly && !matchedProposalIds.has(line.id) ? ' tcp-line-row--unmatched' : ''}`}
        >
          {!isCompareOnly && (
            <input
              type="checkbox"
              checked={proposalSelected.has(line.id)}
              onChange={() => toggleProposalLine(line.id)}
              disabled={readOnly}
            />
          )}
          {line.sourceLineNumber != null && (
            <span
              className="tcp-line-index"
              title={
                line.sourceProductionNumber
                  ? `Line ${line.sourceLineNumber} · Prod# ${line.sourceProductionNumber}`
                  : `Line ${line.sourceLineNumber}`
              }
            >
              #{line.sourceLineNumber}
            </span>
          )}
          <span className="tcp-line-label">
            <span className="tcp-line-name">{line.name}</span>
            {line.species && <span className="tcp-line-species">{line.species}</span>}
          </span>
          <span className="tcp-line-size">
            <span className="tcp-line-dim">{line.dimensions}</span>
            <span className="tcp-line-qty">×{line.quantity}</span>
          </span>
        </label>
      </li>
    );
  };

  const renderCsvSlotRow = (
    slot: CompareAlignedSlot,
    group: CompareDisplayGroup,
    slotIndex: number,
  ) => {
    const csvRow = slot.csvRow;
    if (!csvRow) {
      return (
        <li key={slot.slotKey}>
          <div
            className="tcp-line-row tcp-line-row--spacer"
            aria-hidden="true"
            data-slot={`${group.key}-${slotIndex}`}
          />
        </li>
      );
    }

    const { item, selectionKey } = csvRow;
    return (
      <li key={slot.slotKey}>
        <label
          ref={(el) => {
            if (el) csvRowRefs.current.set(selectionKey, el);
            else csvRowRefs.current.delete(selectionKey);
          }}
          className={`tcp-line-row${!isCompareOnly && csvSelected.has(selectionKey) ? ' tcp-line-row--selected' : ''}${matchedCsvKeys.has(selectionKey) ? ' tcp-line-row--matched' : ''}${isCompareOnly && !matchedCsvKeys.has(selectionKey) ? ' tcp-line-row--unmatched' : ''}`}
        >
          {!isCompareOnly && (
            <input
              type="checkbox"
              checked={csvSelected.has(selectionKey)}
              onChange={() => toggleCsvLine(selectionKey)}
              disabled={readOnly}
            />
          )}
          <span
            className="tcp-line-index"
            title={
              item.productionNumber
                ? `Line ${item.lineNumber} · Prod# ${item.productionNumber}`
                : `Line ${item.lineNumber}`
            }
          >
            #{item.lineNumber}
          </span>
          <span className="tcp-line-name">{item.pieceName}</span>
          <span className="tcp-line-size">
            <span className="tcp-line-dim">{item.normalizedDimensions}</span>
            <span className="tcp-line-qty">×{item.quantity}</span>
          </span>
        </label>
      </li>
    );
  };

  const renderCompareOnlyPanelBody = (side: 'proposal' | 'csv') => {
    if (compareDisplayGroups.length === 0) {
      return (
        <div className="tcp-empty">
          {side === 'proposal' ? 'No timber on proposal yet.' : 'No timber rows parsed from this CSV.'}
        </div>
      );
    }

    return compareDisplayGroups.map((group) => {
      const collapsed = collapsedCompare.has(group.key);
      const lineCount = group.slots.filter((s) =>
        side === 'proposal' ? s.proposalLine : s.csvRow,
      ).length;

      return (
        <article key={`${side}-${group.key}`} className="tcp-group">
          <header className="tcp-group-head">
            <button
              type="button"
              className="tcp-group-toggle"
              onClick={() => toggleCompareGroup(group.key)}
              aria-expanded={!collapsed}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
              <span className="tcp-group-name">{group.structureName}</span>
              <span className="tcp-group-meta">{lineCount} lines</span>
            </button>
          </header>
          {!collapsed && (
            <ul className="tcp-line-list">
              {group.slots.map((slot, slotIndex) =>
                side === 'proposal'
                  ? renderProposalSlotRow(slot, group, slotIndex)
                  : renderCsvSlotRow(slot, group, slotIndex),
              )}
            </ul>
          )}
        </article>
      );
    });
  };

  const selectAllProposal = () => {
    setProposalSelected(new Set(allProposalLineIds));
  };

  const clearAllProposal = () => {
    setProposalSelected(new Set());
  };

  const selectAllCsv = () => {
    setCsvSelected(new Set(allCsvKeys));
  };

  const clearAllCsv = () => {
    setCsvSelected(new Set());
  };

  const toggleCsvGroupSelection = (groupName: string, allSelected: boolean) => {
    const group = csvGroups.find(g => g.groupName === groupName);
    if (!group) return;
    const keys = group.items.map(i => i.selectionKey);
    setCsvSelected(prev => {
      const next = new Set(prev);
      if (allSelected) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      return next;
    });
    if (!allSelected && targetMode === 'new' && !structureName.trim()) {
      setStructureName(groupName);
    }
  };

  const toggleProposalGroupSelection = (nodeSetId: string, allSelected: boolean) => {
    const group = proposalGroups.find(g => g.nodeSetId === nodeSetId);
    if (!group) return;
    const ids = group.lines.map(l => l.id);
    setProposalSelected(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const handleCreate = () => {
    if (!canCreate) return;
    onCreate({
      structureName:
        targetMode === 'existing'
          ? selectedTargetStructure?.name ?? structureName.trim()
          : structureName.trim(),
      targetNodeSetId: targetMode === 'existing' ? targetNodeSetId : undefined,
      proposalLineIds: Array.from(proposalSelected),
      csvLineKeys: Array.from(csvSelected),
    });
  };

  const body = (
    <>
      {!isEmbedded && (
        <header className="tcp-header">
          <div className="tcp-header-text">
            <h2>{resolvedModalTitle}</h2>
            <SourceHeader
              source={source}
              sourceOptions={sourceOptions}
              selectedSourceId={selectedSourceId}
              appliedSourceId={appliedSourceId}
              onSelectSource={onSelectSource}
              sourceSwitchLoading={sourceSwitchLoading}
              readOnly={readOnly}
              onPopoverOpenChange={setSourcePopoverOpen}
            />
          </div>
          <button type="button" className="tcp-close" onClick={onCancel} aria-label="Close" title="Close (Esc)">
            <X size={18} />
          </button>
        </header>
      )}

      {!isCompareOnly && (
        <div className="tcp-toolbar">
          {lockToNodeSetId ? (
            <div className="tcp-target-mode">
              <span className="tcp-name-label">Adding to</span>
              <strong>{selectedTargetStructure?.name ?? 'this structure'}</strong>
            </div>
          ) : (
          <fieldset className="tcp-target-mode">
            <legend className="tcp-name-label">Destination</legend>
            <div className="tcp-target-mode-options">
              <label className="tcp-target-mode-option">
                <input
                  type="radio"
                  name="tcp-target-mode"
                  checked={targetMode === 'new'}
                  onChange={() => setTargetMode('new')}
                  disabled={readOnly}
                />
                New structure
              </label>
              <label className="tcp-target-mode-option">
                <input
                  type="radio"
                  name="tcp-target-mode"
                  checked={targetMode === 'existing'}
                  onChange={() => setTargetMode('existing')}
                  disabled={readOnly || structureOptions.length === 0}
                />
                Existing structure
              </label>
            </div>
          </fieldset>
          )}

          {!lockToNodeSetId && (targetMode === 'new' ? (
            <label className="tcp-name-field">
              <span className="tcp-name-label">New structure name</span>
              <input
                ref={nameInputRef}
                type="text"
                className="tcp-name-input"
                value={structureName}
                onChange={e => setStructureName(e.target.value)}
                placeholder="e.g. Poolhouse beams"
                disabled={readOnly}
                maxLength={120}
              />
            </label>
          ) : (
            <label className="tcp-name-field">
              <span className="tcp-name-label">Existing structure</span>
              <select
                className="tcp-name-input tcp-structure-select"
                value={targetNodeSetId}
                onChange={e => setTargetNodeSetId(e.target.value)}
                disabled={readOnly || structureOptions.length === 0}
              >
                <option value="">Select a structure…</option>
                {structureOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
          ))}

          <div className="tcp-summary" role="status">
            <span className="tcp-summary-stat">
              <strong>{totalSelected}</strong> line{totalSelected === 1 ? '' : 's'} selected
            </span>
            {connectorPairs.length > 0 && (
              <span className="tcp-summary-stat tcp-summary-stat--match">
                <strong>{connectorPairs.length}</strong> matching line{connectorPairs.length === 1 ? '' : 's'}
              </span>
            )}
            {duplicateMatchKeys.length > 0 && (
              <span className="tcp-summary-warn" title="Same name, size, and quantity selected on both sides — both copies will be included">
                <AlertTriangle size={14} aria-hidden />
                {duplicateMatchKeys.length} duplicate piece{duplicateMatchKeys.length === 1 ? '' : 's'} on both sides
              </span>
            )}
          </div>
        </div>
      )}

      {isCompareOnly && (
        <div className="tcp-compare-bar" role="status">
          {connectorPairs.length > 0 ? (
            <span className="tcp-summary-stat tcp-summary-stat--match">
              <strong>{connectorPairs.length}</strong> matching line{connectorPairs.length === 1 ? '' : 's'}
            </span>
          ) : (
            <span className="tcp-summary-stat">No matching lines</span>
          )}
          <span className="tcp-compare-bar-label">
            {leftPanelTitle} vs {rightPanelTitle}
          </span>
        </div>
      )}

      <div className="tcp-panels-wrap" ref={panelsWrapRef}>
          {connectorPaths.length > 0 && (
            <svg className="tcp-connectors" aria-hidden="true">
              {connectorPaths.map(({ id, d }) => (
                <path key={id} className="tcp-connector-path" d={d} />
              ))}
            </svg>
          )}

          <div className={`tcp-panels${swapPanels ? ' tcp-panels--swapped' : ''}`}>
          <section className="tcp-panel tcp-panel--proposal">
            <div className="tcp-panel-head">
              <h3>{proposalPanelTitle}</h3>
              {!isCompareOnly && !readOnly && allProposalLineIds.length > 0 && (
                <div className="tcp-panel-actions">
                  <button type="button" className="tcp-link-btn" onClick={selectAllProposal}>Select all</button>
                  <button type="button" className="tcp-link-btn" onClick={clearAllProposal}>Clear</button>
                </div>
              )}
            </div>
            <div className="tcp-panel-body">
              {isCompareOnly ? (
                renderCompareOnlyPanelBody('proposal')
              ) : proposalGroups.length === 0 ? (
                <div className="tcp-empty">No timber on proposal yet — pick lines from the CSV.</div>
              ) : (
                proposalGroups.map(group => {
                  const collapsed = collapsedProposal.has(group.nodeSetId);
                  const groupLineIds = group.lines.map(l => l.id);
                  const selectedInGroup = groupLineIds.filter(id => proposalSelected.has(id)).length;
                  return (
                    <article key={group.nodeSetId} className="tcp-group">
                      <header className="tcp-group-head">
                        {!readOnly && (
                          <GroupSelectCheckbox
                            checked={selectedInGroup === group.lines.length && group.lines.length > 0}
                            indeterminate={selectedInGroup > 0 && selectedInGroup < group.lines.length}
                            label={`Select all ${group.structureName}`}
                            onChange={() =>
                              toggleProposalGroupSelection(
                                group.nodeSetId,
                                selectedInGroup === group.lines.length && group.lines.length > 0,
                              )
                            }
                          />
                        )}
                        <button
                          type="button"
                          className="tcp-group-toggle"
                          onClick={() => toggleProposalGroup(group.nodeSetId)}
                          aria-expanded={!collapsed}
                        >
                          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                          <span className="tcp-group-name">{group.structureName}</span>
                          <span className="tcp-group-meta">
                            {selectedInGroup > 0 && `${selectedInGroup}/`}
                            {group.lines.length} lines
                          </span>
                        </button>
                      </header>
                      {!collapsed && (
                        <ul className="tcp-line-list">
                          {group.lines.map(line => (
                            <li key={line.id}>
                              <label
                                ref={(el) => {
                                  if (el) proposalRowRefs.current.set(line.id, el);
                                  else proposalRowRefs.current.delete(line.id);
                                }}
                                className={`tcp-line-row${!isCompareOnly && proposalSelected.has(line.id) ? ' tcp-line-row--selected' : ''}${matchedProposalIds.has(line.id) ? ' tcp-line-row--matched' : ''}${isCompareOnly && !matchedProposalIds.has(line.id) ? ' tcp-line-row--unmatched' : ''}`}
                              >
                                {!isCompareOnly && (
                                  <input
                                    type="checkbox"
                                    checked={proposalSelected.has(line.id)}
                                    onChange={() => toggleProposalLine(line.id)}
                                    disabled={readOnly}
                                  />
                                )}
                                {line.sourceLineNumber != null && (
                                  <span
                                    className="tcp-line-index"
                                    title={
                                      line.sourceProductionNumber
                                        ? `Line ${line.sourceLineNumber} · Prod# ${line.sourceProductionNumber}`
                                        : `Line ${line.sourceLineNumber}`
                                    }
                                  >
                                    #{line.sourceLineNumber}
                                  </span>
                                )}
                                <span className="tcp-line-label">
                                  <span className="tcp-line-name">{line.name}</span>
                                  {line.species && (
                                    <span className="tcp-line-species">{line.species}</span>
                                  )}
                                </span>
                                <span className="tcp-line-size">
                                  <span className="tcp-line-dim">{line.dimensions}</span>
                                  <span className="tcp-line-qty">×{line.quantity}</span>
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>
                  );
                })
              )}
            </div>
          </section>

          <section className="tcp-panel tcp-panel--csv">
            <div className="tcp-panel-head">
              <h3>{csvPanelTitle}</h3>
              {!isCompareOnly && !readOnly && allCsvKeys.length > 0 && (
                <div className="tcp-panel-actions">
                  <button type="button" className="tcp-link-btn" onClick={selectAllCsv}>Select all</button>
                  <button type="button" className="tcp-link-btn" onClick={clearAllCsv}>Clear</button>
                </div>
              )}
            </div>
            <div className="tcp-panel-body">
              {isCompareOnly ? (
                renderCompareOnlyPanelBody('csv')
              ) : csvGroups.length === 0 ? (
                <div className="tcp-empty">No timber rows parsed from this CSV.</div>
              ) : (
                csvGroups.map(group => {
                  const collapsed = collapsedCsv.has(group.groupName);
                  const selectedInGroup = group.items.filter(i => csvSelected.has(i.selectionKey)).length;
                  return (
                    <article key={group.groupName} className="tcp-group">
                      <header className="tcp-group-head">
                        {!readOnly && (
                          <GroupSelectCheckbox
                            checked={selectedInGroup === group.items.length && group.items.length > 0}
                            indeterminate={selectedInGroup > 0 && selectedInGroup < group.items.length}
                            label={`Select all ${group.groupName}`}
                            onChange={() =>
                              toggleCsvGroupSelection(
                                group.groupName,
                                selectedInGroup === group.items.length && group.items.length > 0,
                              )
                            }
                          />
                        )}
                        <button
                          type="button"
                          className="tcp-group-toggle"
                          onClick={() => toggleCsvGroup(group.groupName)}
                          aria-expanded={!collapsed}
                        >
                          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                          <span className="tcp-group-name">{group.groupName}</span>
                          <span className="tcp-group-meta">
                            {selectedInGroup > 0 && `${selectedInGroup}/`}
                            {group.items.length} lines
                          </span>
                        </button>
                      </header>
                      {!collapsed && (
                        <ul className="tcp-line-list">
                          {group.items.map(({ item, selectionKey }) => (
                            <li key={selectionKey}>
                              <label
                                ref={(el) => {
                                  if (el) csvRowRefs.current.set(selectionKey, el);
                                  else csvRowRefs.current.delete(selectionKey);
                                }}
                                className={`tcp-line-row${!isCompareOnly && csvSelected.has(selectionKey) ? ' tcp-line-row--selected' : ''}${matchedCsvKeys.has(selectionKey) ? ' tcp-line-row--matched' : ''}${isCompareOnly && !matchedCsvKeys.has(selectionKey) ? ' tcp-line-row--unmatched' : ''}`}
                              >
                                {!isCompareOnly && (
                                  <input
                                    type="checkbox"
                                    checked={csvSelected.has(selectionKey)}
                                    onChange={() => toggleCsvLine(selectionKey)}
                                    disabled={readOnly}
                                  />
                                )}
                                <span
                                  className="tcp-line-index"
                                  title={
                                    item.productionNumber
                                      ? `Line ${item.lineNumber} · Prod# ${item.productionNumber}`
                                      : `Line ${item.lineNumber}`
                                  }
                                >
                                  #{item.lineNumber}
                                </span>
                                <span className="tcp-line-name">{item.pieceName}</span>
                                <span className="tcp-line-size">
                                  <span className="tcp-line-dim">{item.normalizedDimensions}</span>
                                  <span className="tcp-line-qty">×{item.quantity}</span>
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>
                  );
                })
              )}
            </div>
          </section>
          </div>
        </div>

      {!isCompareOnly && (
        <footer className="tcp-footer">
          <p className="tcp-footer-hint">
            {targetMode === 'existing'
              ? lockToNodeSetId
                ? 'Selected lines are appended to this structure. Lines already on it are skipped when picked from the proposal side.'
                : 'Selected lines are appended to the chosen structure. Lines already on that structure are skipped when picked from the proposal side.'
              : 'Selected lines are copied into a new structure — existing structures are not modified.'}
          </p>
          <div className="tcp-footer-spacer" />
          <button type="button" className="tcp-btn tcp-btn--ghost" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="tcp-btn tcp-btn--primary"
            onClick={handleCreate}
            disabled={!canCreate}
            title={readOnly ? 'Proposal is locked' : undefined}
          >
            {readOnly
              ? 'Proposal locked'
              : totalSelected === 0
                ? 'Select lines to continue'
                : targetMode === 'existing'
                  ? !targetNodeSetId
                    ? 'Select a structure'
                    : `Add to ${selectedTargetStructure?.name ?? 'structure'} (${totalSelected} line${totalSelected === 1 ? '' : 's'})`
                  : !structureName.trim()
                    ? 'Enter a structure name'
                    : `Create structure (${totalSelected} line${totalSelected === 1 ? '' : 's'})`}
          </button>
        </footer>
      )}
    </>
  );

  if (isEmbedded) {
    return (
      <div className="tcp-embedded" aria-label={`Compare ${proposalPanelTitle} to ${csvPanelTitle}`}>
        {body}
      </div>
    );
  }

  return (
    <div className="tcp-overlay" role="dialog" aria-modal="true" aria-label="Compare and pick timber lines">
      <div className="tcp-modal">
        {body}
      </div>
    </div>
  );
};

export default TimberComparePickModal;
